import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { deserializeModel as deserializeMediaJob } from "../edit/publications_modal_body/serialization";

const MediaProfileContext = React.createContext(null);

const CONTACT_OWNER_TYPE = "Id::Profile::MediaProfile";

// Keep media jobs ordered most-recent-first (matches the server order in getAllByMediaProfile, which
// requests order=started_at.desc), so an optimistic insert lands in the right place before silentReload
// reconciles. Media jobs have no `featured` flag (unlike PR employment), so started_at is the only key.
function mediaJobsInvariant(rows) {
  return [...rows].sort((a, b) => (b.from || "").localeCompare(a.from || "")); // most-recent started_at first
}

// Dedupe a media profile's derived tags by hashtag_id with a times_tagged count, most-tagged first
// (the same shaping the PR provider does; media has only derived tags — no explicitly-added tags).
function dedupeTags(rows) {
  return rows
    .reduce((acc, current) => {
      const existing = acc.find((item) => item.hashtag_id === current.hashtag_id);
      if (existing) {
        existing.times_tagged += 1;
      } else {
        acc.push({ ...current, times_tagged: 1 });
      }
      return acc;
    }, [])
    .sort((a, b) => b.times_tagged - a.times_tagged);
}

// Media Profile's state provider — the shared provider/context pattern (see profiles/README.md).
// Owns the profile record, its Writes-On hashtags, and its media jobs (publications) collection.
// The media jobs are stored DESERIALIZED (camelCase) through the edit-modal serializer, so the show
// grid + the modal share one shape — and the exposed setMediaJobs is invariant-preserving so
// optimistic mutations from the edit surface stay correctly ordered.
function MediaProfileProvider(props) {
  const { children } = props;

  // ViewerContext seed ({ editable, isSelf, isAuthenticated, planPaid, role }) computed server-side
  // and threaded ERB -> provider -> affordances. Default {} so a missing prop reads as
  // non-editable.
  // eslint-disable-next-line react/destructuring-assignment
  const { viewerContext = {} } = props.value;

  // Mirrors the requests RPC's entitlement gate (admin, the profile's own user, or a paid plan)
  // as a fetch-avoidance hint only: the server re-derives entitlement from the JWT and stays
  // authoritative, so a stale/wrong hint here can waste or skip a fetch but never leak rows.
  // The own-user arm reads isSelf, not editable: an owner who cannot edit still reads their own
  // requests.
  const mayViewRequests =
    !!viewerContext.isAuthenticated &&
    (!!viewerContext.planPaid || !!viewerContext.isSelf || viewerContext.role === "admin");

  // Whether this viewer may edit the profile (Id::ProfilePolicy#update?, seeded server-side).
  const editable = !!viewerContext.editable;

  // alphabetized
  const [asyncError, setAsyncError] = useState(null);
  const [contactInfos, setContactInfos] = useState([]);
  const [generalError, setGeneralError] = useState(null);
  const [hashtags, setHashtags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [mediaJobs, setMediaJobsRaw] = useState([]);
  const [mediaProfile, setMediaProfile] = useState(null);
  const [mediaProfileId, setMediaProfileId] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(null);

  // Invariant-preserving media-jobs setter: the action hook mutates via this so optimistic inserts stay
  // ordered most-recent-first. Accepts a value or an updater (functional setState).
  const setMediaJobs = useCallback(
    (next) => setMediaJobsRaw((current) => mediaJobsInvariant(typeof next === "function" ? next(current) : next)),
    [],
  );

  // The full data load. `silent` skips the page-level loading flag so a refetch doesn't flash the
  // skeleton (same split as the PR/Company providers).
  const loadProfile = useCallback(
    async ({ silent = false, nonfatal = false } = {}) => {
      if (!mediaProfileId) return;
      try {
        if (!silent) setIsLoading(true);

        const profilePromise = qapi.mediaProfiles.get(mediaProfileId).then((response) => {
          // Gate on the returned row, not the estimated count (count=estimated can misreport).
          const rows = Array.isArray(response.data) ? response.data : [];
          setMediaProfile(rows.length ? rows[0] : null);
        });

        const jobsPromise = qapi.mediaJobs.getAllByMediaProfile(mediaProfileId).then((response) => {
          const rows = Array.isArray(response.data) ? response.data : [];
          setMediaJobs(rows.map(deserializeMediaJob));
        });

        const tagsPromise = qapi.hashtags
          .getAllMediaProfileDerivedTags(mediaProfileId)
          .then((response) => {
            const rows = Array.isArray(response.data) ? response.data : [];
            setHashtags(dedupeTags(rows));
          })
          .catch(() => setHashtags([]));

        // The profile row's contact_details aggregate leaves out email and phone for every viewer,
        // which are exactly the details the profile's own user needs to edit. Fetch the raw rows
        // only for a viewer who may edit the profile; a public viewer keeps reading the aggregate,
        // so their page makes no extra request and shows what it shows today. This read is
        // enrichment, not page-critical, so a failure (or a non-array response) degrades to [].
        const contactsPromise = editable
          ? qapi.contactInfos
              .getAllByContactOwner(mediaProfileId, CONTACT_OWNER_TYPE)
              .then((response) => {
                if (response.error || !Array.isArray(response.data)) {
                  setContactInfos([]);
                  return;
                }
                setContactInfos(response.data);
              })
              .catch(() => setContactInfos([]))
          : Promise.resolve(setContactInfos([]));

        if (nonfatal) {
          // Every started read must settle (and commit its state) before loadProfile resolves: callers
          // treat resolution as "all state writes are done". Promise.all rejects on the first rejection
          // while siblings are still in flight, so an early rejection here — with a superseded read still
          // pending — could let that late read commit stale state over newer state after a retry. Settle
          // all four, then rethrow the first rejection so the catch below keeps its exact behavior. (Only
          // the profile and jobs reads can reject; tags already self-absorb their errors.)
          const results = await Promise.allSettled([profilePromise, jobsPromise, tagsPromise, contactsPromise]);
          const rejected = results.find((r) => r.status === "rejected");
          if (rejected) throw rejected.reason;
        } else {
          // Fatal loads (initial page load, post-save refresh) must surface the error boundary
          // immediately rather than idle on the skeleton behind a stalled sibling read (the fetch layer
          // has no timeout). Fail fast on the first rejection. The stale-late-write race doesn't apply
          // here because a fatal reload failure tears the page down — there is no retry surface to race.
          await Promise.all([profilePromise, jobsPromise, tagsPromise, contactsPromise]);
        }
      } catch (error) {
        // A nonfatal reload is used when reconciling after a failed save — a failed refresh there must
        // not escalate a recoverable modal error into the page error boundary (asyncError is re-thrown).
        if (!nonfatal) setAsyncError(error);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [mediaProfileId, editable, setMediaJobs],
  );

  // Initial load (and reload whenever the id changes).
  useEffect(() => {
    if (mediaProfileId) loadProfile();
  }, [mediaProfileId, loadProfile]);

  // Silent refetch (edit flows call this after a save). Forwards options so a caller can mark the
  // reload nonfatal (e.g. reconciling after a failed save); default call sites are unaffected.
  const silentReload = useCallback((opts = {}) => loadProfile({ silent: true, ...opts }), [loadProfile]);

  const providerValue = useMemo(
    () => ({
      // alphabetized
      asyncError,
      contactInfos,
      generalError,
      hashtags,
      isLoading,
      isUpdating,
      mayViewRequests,
      mediaJobs,
      mediaProfile,
      mediaProfileId,
      openEditModal,
      setAsyncError,
      setGeneralError,
      setIsUpdating,
      setMediaJobs,
      setMediaProfileId,
      setOpenEditModal,
      silentReload,
      viewerContext,
    }),
    [
      asyncError,
      contactInfos,
      generalError,
      hashtags,
      isLoading,
      isUpdating,
      mayViewRequests,
      mediaJobs,
      mediaProfile,
      mediaProfileId,
      openEditModal,
      setMediaJobs,
      silentReload,
      viewerContext,
    ],
  );

  return <MediaProfileContext.Provider value={providerValue}>{children}</MediaProfileContext.Provider>;
}

export { MediaProfileContext, MediaProfileProvider, CONTACT_OWNER_TYPE };

MediaProfileProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
